import { Component } from '@theme/component';
import {
  setSelectedModel,
  getSelectedModel,
  clearSelectedModel,
  MODEL_CHANGED_EVENT,
} from '@theme/model-preference';

/**
 * Header phone-model dropdown (BURGA pattern). Picking a model stores it as
 * the shopper's remembered preference; it doesn't filter this page's content
 * (every case fits every model in the current variant-based catalog) — see
 * @theme/model-personalization for where the preference actually gets used.
 *
 * The dropdown also follows the preference when something else changes it, so
 * choosing a model in the PDP's own variant picker is reflected up here rather
 * than leaving the header advertising the shopper's previous phone.
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} select
 * @extends {Component<Refs>}
 */
class ModelPickerComponent extends Component {
  requiredRefs = ['select'];

  #onModelChanged = () => {
    const model = getSelectedModel();
    // '' selects the blank "Your phone" option, which is what a cleared
    // preference should look like.
    this.refs.select.value = model ?? '';
  };

  connectedCallback() {
    super.connectedCallback();
    const stored = getSelectedModel();
    if (stored) this.refs.select.value = stored;
    document.addEventListener(MODEL_CHANGED_EVENT, this.#onModelChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(MODEL_CHANGED_EVENT, this.#onModelChanged);
  }

  /** @param {Event} event */
  select(event) {
    const value = /** @type {HTMLSelectElement} */ (event.target).value;

    if (!value) {
      // Reselecting the blank "Your phone" option forgets the preference.
      // clearSelectedModel notifies @theme/model-personalization, which restores
      // the server-rendered links in place, so no reload is needed.
      clearSelectedModel();
      return;
    }

    setSelectedModel(value);
  }
}

if (!customElements.get('model-picker-component')) {
  customElements.define('model-picker-component', ModelPickerComponent);
}
